import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaTools, FaBuilding, FaLayerGroup, FaCalendar, FaComment, FaAngleDown, FaRulerCombined } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';

import axios from "axios";


const API_PATH = 'https://buildx-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const GetQuote = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    serviceType: '',
    buildingType: '',
    totalArea: '',
    materialType: '',
    address: '',
    startDate: '',
    endDate: '',
    comment: '',
    extraServices: []
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.serviceType) validationErrors.push('Service type is required');
      if (!formData.buildingType) validationErrors.push('Building type is required');
      if (!formData.totalArea) validationErrors.push('Total area is required');
      if (!formData.materialType) validationErrors.push('Material type is required');
      if (!formData.address) validationErrors.push('Address is required');
      if (!formData.startDate) validationErrors.push('Start date is required');
      if (!formData.endDate) validationErrors.push('End date is required');
      if (!formData.comment) validationErrors.push('Project details are required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => postData.append(`${key}[]`, item));
        } else {
          postData.append(key, value);
        }
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        serviceType: '',
        buildingType: '',
        totalArea: '',
        materialType: '',
        address: '',
        startDate: '',
        endDate: '',
        comment: '',
        extraServices: []
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, extraServices: [...prev.extraServices, value] };
      } else {
        return { ...prev, extraServices: prev.extraServices.filter(item => item !== value) };
      }
    });
  };

  return (
    <section className="custom-form get-quote linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Request A Quote'} subTitle={"Get Your Construction Estimate"} linear sideHeading />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Service Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="service-type"><FaTools /></label>
                  <select
                    className="select-box"
                    id="service-type"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Construction Service</option>
                    <option value="residential">Residential Construction</option>
                    <option value="commercial">Commercial Construction</option>
                    <option value="renovation">Renovation & Remodeling</option>
                    <option value="foundation">Foundation Work</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">

              {/* Building Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="building-type"><FaBuilding /></label>
                  <select
                    className="select-box"
                    id="building-type"
                    name="buildingType"
                    value={formData.buildingType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Building Type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {/* Total Area Input Field */}
              <div className="input-field">
                <label htmlFor="total-area"><FaRulerCombined /></label>
                <input
                  type="number"
                  name="totalArea"
                  className="box"
                  id="total-area"
                  placeholder="Total Area (sq ft)"
                  value={formData.totalArea}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Construction Material Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="material-type"><FaLayerGroup /></label>
                  <select
                    className="select-box"
                    id="material-type"
                    name="materialType"
                    value={formData.materialType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Material</option>
                    <option value="concrete">Concrete</option>
                    <option value="steel">Steel</option>
                    <option value="wood">Wood</option>
                    <option value="brick">Brick</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              {/* Address Input Field */}
              <div className="input-field">
                <label htmlFor="address"><FaLocationDot /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Project Location"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Start Date Input Field */}
              <div className="input-field">
                <label htmlFor="start-date"><FaCalendar /></label>
                <input
                  type="text"
                  name="startDate"
                  className="box"
                  id="start-date"
                  placeholder="Start Date"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* End Date Input Field */}
              <div className="input-field">
                <label htmlFor="end-date"><FaCalendar /></label>
                <input
                  type="text"
                  name="endDate"
                  className="box"
                  id="end-date"
                  placeholder="End Date"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Comment Text Area */}
            <div className="input-field text-area">
              <label htmlFor="comment"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="comment"
                className="box"
                id="comment"
                placeholder="Project Details"
                value={formData.comment}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Extra Services Checkboxes */}
            <div className="extra-services">

              {/* Plumbing Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="plumbing"
                  name="extraServices[]"
                  value="Plumbing"
                  checked={formData.extraServices.includes('Plumbing')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="plumbing">Plumbing</label>
                <div className="checkbox-indicator"></div>
              </div>

              {/* Electrical Work Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="electrical"
                  name="extraServices[]"
                  value="Electrical Work"
                  checked={formData.extraServices.includes('Electrical Work')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="electrical">Electrical Work</label>
                <div className="checkbox-indicator"></div>
              </div>

              {/* Interior Finishing Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="finishing"
                  name="extraServices[]"
                  value="Interior Finishing"
                  checked={formData.extraServices.includes('Interior Finishing')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="finishing">Interior Finishing</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote Request Submitted Successfully</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;